// LoginCallback.jsx

import { useEffect } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";

import "@esri/calcite-components/components/calcite-loader";

import { useAuthBiogems } from "@cores/security/hooks/useAuthBiogems";
import { useWellSelectionStore } from '@cores/stores/useWellSelectionStore';

import {
  LOCAL_STORAGE_KEYS,
  LOG_MODULES
} from "@/config/config";

import { startSession } from "@libs/utils/monitor";
import { log } from "@libs/utils/helper.js";

const LoginCallback = () => {
  const navigate = useNavigate();
  const { setAuthState } = useAuthBiogems();
  const [searchParams] = useSearchParams();

  // Route params may exist depending on the authentication callback config.
  useParams();

  useEffect(() => {
    const authData = window.authData;

    const isCorruptedUrl =
      window.location.pathname.includes(
        "[object%20Object]"
      );

    // Get provider flag from the URL query string.
    const currentProvider =
      searchParams.get("provider") ||
      "logingov";

    /**
     * Handles corrupted authentication callback URLs.
     * Attempts a best-effort session recovery before redirecting.
     */
    const handleCorruptedUrl = (
      provider
    ) => {
      const user = parseUserObject(
        authData?.userData
      );

      if (isValidUser(user)) {
        try {
          log(
            LOG_MODULES.AUTH,
            "LoginCallback: Recovering session from corrupted URL.",
            user
          );

          saveUserToLocalStorage(
            user,
            provider
          );

          startSession();
        } catch (error) {
          console.error(
            "LoginCallback: Error during corrupted URL recovery:",
            error
          );
        }
      }

      console.error(
        "LoginCallback: Corrupted URL detected. Redirecting to root."
      );

      window.location.replace("/");
    };

    /**
     * Handles the standard authentication callback flow.
     * Clears returnTo before processing so stale paths do not persist.
     */
    const handleNormalAuthAsync = async (
      provider
    ) => {
      const returnTo =
        localStorage.getItem("returnTo");

      localStorage.removeItem("returnTo");

      try {
        const success =
          await processAuthAndSetStateAsync(
            authData,
            setAuthState,
            provider
          );

        if (success) {
          navigate(
            returnTo || "/",
            {
              replace: true
            }
          );
        } else {
          clearAuthState(setAuthState);

          navigate(
            "/",
            {
              replace: true
            }
          );
        }
      } catch (error) {
        console.error(
          "LoginCallback: Error processing auth callback:",
          error
        );

        clearAuthState(setAuthState);

        navigate(
          "/",
          {
            replace: true
          }
        );
      }
    };

    if (isCorruptedUrl) {
      handleCorruptedUrl(
        currentProvider
      );

      return;
    }

    handleNormalAuthAsync(
      currentProvider
    );
  }, [
    navigate,
    searchParams,
    setAuthState
  ]);

  return (
    <calcite-loader
      active
      label="Loading and verifying credentials..."
    />
  );
};

export default LoginCallback;

/**
 * Safely parses user data from the authentication payload.
 * Accepts either a JSON string or a plain object.
 */
const parseUserObject = (userData) => {
  if (!userData) {
    return null;
  }

  try {
    return typeof userData === "string"
      ? JSON.parse(userData)
      : userData;
  } catch (error) {
    console.error(
      "LoginCallback: Failed to parse userData:",
      error
    );

    return null;
  }
};

/**
 * Validates that the minimum required authentication fields are present.
 */
const isValidUser = (user) => {
  return Boolean(
    user &&
    user.access_token &&
    user.user_key
  );
};

/**
 * Persists authenticated user information to localStorage.
 */
const saveUserToLocalStorage = (
  user,
  provider
) => {
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.NIOGEMS.USER,
    JSON.stringify(user)
  );

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.NIOGEMS.TOKEN,
    user.access_token
  );

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.NIOGEMS.USERKEY,
    user.user_key
  );

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.NIOGEMS.AUTH_PROVIDER,
    provider
  );
};

/**
 * Clears authentication state on failure or unexpected conditions.
 */
const clearAuthState = (
  setAuthState
) => {
  setAuthState((prev) => ({
    ...prev,
    user: null,
    loading: false,
    provider: null
  }));
};

/**
 * Validates, persists, and sets authentication state from a window.authData
 * payload. Returns true on success and false otherwise.
 */
const processAuthAndSetStateAsync = async (
  authData,
  setAuthState,
  provider
) => {
  const user = parseUserObject(
    authData?.userData
  );

  if (!isValidUser(user)) {
    console.error(
      "LoginCallback: Invalid or missing user auth data."
    );

    return false;
  }

  saveUserToLocalStorage(
    user,
    provider
  );

  startSession();

  setAuthState((prev) => ({
    ...prev,
    user,
    loading: false,
    provider
  }));

  log(
    LOG_MODULES.AUTH,
    "LoginCallback Reservation user.reservations:",
    user.reservations
  );

  await useWellSelectionStore
    .getState()
    .fetchAndBuildDefExpressAsync(
      user.reservations
    );

  return true;
};